import React from 'react';
import { DonationFundPlatformTypeEnum } from '@graphql/generated';
import { Box, Flex, FormControl, InputV2, LinkV1, SelectV1 } from '@withjoy/joykit';
import { StyledInputPrefix } from '@apps/registry/common/components/AddCashFund/AddCashFundDialog.styles';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import { getPaymentLink } from '@apps/registry/common/components/AddCashFund/helpers';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { PaymentMethodOption, paymentMethodOptions } from '@shared/utils/paymentMethodOptions';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { FormikProps } from 'formik';
import { EditGiftDailogFields } from '../../types';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { CookedProduct } from '@apps/registry/common/selectors/ProductListSelector';

export interface GroupGiftPaymentMethodProps {
  setPaymentMethod: (paymentMethod: DonationFundPlatformTypeEnum | undefined) => void;
  setPaymentMethodUsername: (username: string | undefined) => void;
  selectedPaymentMethod: Maybe<PaymentMethodOption>;
  formik: FormikProps<EditGiftDailogFields>;
  product: CookedProduct | null;
  currency: string;
}

const localPaymentOptions = paymentMethodOptions.filter(method => method.value !== 'cashOrCheck' && method.value !== 'joyCredit');

export const GroupGiftPaymentMethod: React.FC<GroupGiftPaymentMethodProps> = ({ setPaymentMethod, setPaymentMethodUsername, selectedPaymentMethod, formik, product, currency }) => {
  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();
  const { paymentMethodLabel } = getCashFundDialogTranslations();
  const { errors } = formik;
  const { groupGiftingPaymentMethodSelect } = useAdminRegistryTelemetry();

  return (
    <Box marginTop={6}>
      <Flex
        alignItems="flex-start"
        justifyContent="center"
        flexDirection={['column', 'column', 'row']}
        rowGap={4}
        columnGap={4}
      >
        <FormControl
          error={formik.touched.paymentMethod && errors.paymentMethod ? errors.paymentMethod : undefined}
          width={['100%', '100%', '50%']}
        >
          <>
            <SelectV1
              placeholder={paymentMethodLabel}
              searchable={false}
              options={localPaymentOptions}
              value={selectedPaymentMethod}
              onChange={e => {
                setPaymentMethod(e?.value as DonationFundPlatformTypeEnum);
                groupGiftingPaymentMethodSelect({ product, paymentMethod: e?.value });
              }}
            />
            <LinkV1
              href={getPaymentLink(formik.values.paymentMethod, formik.values.username)}
              target="_blank"
              underline="none"
              style={{
                fontSize: '13px',
                lineHeight: '20px',
                opacity: formik.values.username ? 1 : 0,
                display: errors.username && (formik.touched.paymentMethod || formik.touched.username) ? 'none' : 'block',
              }}
              tabIndex={0}
            >
              {getPaymentLink(formik.values.paymentMethod, formik.values.username)}
            </LinkV1>
          </>
        </FormControl>
        <FormControl
          error={formik.touched.username && errors.username ? errors.username : undefined}
          isInvalid={!!formik.touched.username && !!errors.username}
          width={['100%', '100%', '50%']}
        >
          <>
            <Flex
              alignItems="center"
              overflow="hidden"
              position="relative"
              width={['100%', 'unset', 'unset']}
            >
              <InputV2
                {...formik.getFieldProps('username')}
                placeholder="username"
                tabIndex={0}
                style={{ padding: '0 16px 0 28px' }}
                onChange={e => setPaymentMethodUsername(e?.target.value)}
              />
              <StyledInputPrefix
                isFixedPrefix
                prefixOffset={15}
              >
                {formik.values.paymentMethod === DonationFundPlatformTypeEnum.cashapp ? getCurrencySymbolFromCurrencyCode(currency || DEFAULT_CURRENCY) : '@'}
              </StyledInputPrefix>
            </Flex>
          </>
        </FormControl>
      </Flex>
    </Box>
  );
};
